import { useMemo, type ReactNode } from 'react';
import type { FilterListProps } from './types';
import { chipListGenerator } from './chipListGenerator';
import { InputKeywordSearch } from './InputKeyworkdSearch/InputKeywordSearch';
import { useSearchBar } from './hooks/useSearchBar';
import { HorizontalRadioList } from './components/HorizontalRadioList';
import { HorizontalChipList } from './components/HorizontalChipList';
import { InlineOneCheckbox } from './components/InlineOneCheckbox';
import { Flex } from '@banhala/ui-kit';

type SequencedItem = {
  sequence: number;
  key: string;
  hasDivider?: boolean;
  render: () => ReactNode;
};

export const FilterList = ({
  body,
  logging,
  scrollToFilterList,
}: FilterListProps) => {
  const searchChip = body.chip?.chip_list.find(chip => chip.type === 'search');

  const {
    isSearchBarOpen,
    toggleSearchBar,
    inputValue,
    setInputValue,
    closeFilterList,
  } = useSearchBar({
    searchChip,
    scrollToFilterList,
  });

  const filterChips = useMemo(() => {
    if (body.chip?.chip_list == null) return [];
    return chipListGenerator(body.chip.chip_list);
  }, [body.chip?.chip_list]);

  // sequence 기반 렌더 아이템 수집 + 정렬
  const sequencedItems = useMemo(() => {
    const items: SequencedItem[] = [];

    if (body.radio) {
      items.push({
        sequence: body.radio.sequence ?? 0,
        key: 'radio',
        hasDivider: body.radio.has_divider,
        render: () => (
          <HorizontalRadioList
            radioFilter={body.radio!}
            logging={logging}
            scrollToFilterList={scrollToFilterList}
            eventName="CLICK_FILTER"
          />
        ),
      });
    }

    if (body.sub_radio) {
      items.push({
        sequence: body.sub_radio.sequence ?? 1,
        key: 'sub_radio',
        hasDivider: body.sub_radio.has_divider,
        render: () => (
          <HorizontalRadioList
            radioFilter={body.sub_radio!}
            logging={logging}
            scrollToFilterList={scrollToFilterList}
            eventName="CLICK_SUB_FILTER"
          />
        ),
      });
    }

    if (body.one_checkbox) {
      items.push({
        sequence: body.one_checkbox.sequence ?? 3,
        key: 'one_checkbox',
        render: () => (
          <InlineOneCheckbox
            oneCheckbox={body.one_checkbox!}
            logging={logging}
            scrollToFilterList={scrollToFilterList}
          />
        ),
      });
    }

    if (filterChips.length > 0) {
      items.push({
        sequence: body.chip?.sequence ?? 4,
        key: 'chip',
        hasDivider: body.chip?.has_divider,
        render: () => (
          <>
            <HorizontalChipList
              filterChips={filterChips}
              showResetChip={body.chip?.refreshable ?? false}
              isSearchBarOpen={isSearchBarOpen}
              toggleSearchBar={toggleSearchBar}
              closeSearchBar={closeFilterList}
              logging={logging}
              scrollToFilterList={scrollToFilterList}
              radioQueryName={body.radio?.query_name}
            />
            {isSearchBarOpen && (
              <InputKeywordSearch
                inputValue={inputValue}
                setInputValue={setInputValue}
                placeholder={searchChip?.search_hint}
                logging={logging}
                closeFilterList={closeFilterList}
              />
            )}
          </>
        ),
      });
    }

    return items.sort((a, b) => a.sequence - b.sequence);
  }, [
    body,
    logging,
    scrollToFilterList,
    filterChips,
    isSearchBarOpen,
    toggleSearchBar,
    closeFilterList,
    inputValue,
    setInputValue,
    searchChip,
  ]);

  return (
    <Flex backgroundColor="white">
      {sequencedItems.map(item => (
        <Flex
          key={item.key}
          borderBottomWidth={item.hasDivider !== false ? 1 : 0}
          borderColor="border_tertiary"
        >
          {item.render()}
        </Flex>
      ))}
    </Flex>
  );
};
